import { useState } from "react";
import api from "../../services/api";

const ALL_DOC_TYPES = [
  { key: "aadhaar", label: "Aadhaar Card", required: true },
  { key: "pan", label: "PAN Card", required: true },
  { key: "photo", label: "Passport Photo", required: true },
  { key: "driving_license", label: "Driving License", required: false },
  { key: "passport", label: "Passport", required: false },
  { key: "marksheet_10", label: "10th Marksheet", required: false },
  { key: "marksheet_12", label: "12th Marksheet", required: false },
  { key: "offer_letter", label: "Offer Letter", required: false },
  { key: "internship_letter", label: "Internship Letter", required: false },
  { key: "nda", label: "NDA", required: false },
  { key: "college_id", label: "College ID", required: false },
];

const HR_UPLOAD_TYPES = [
  { key: "offer_letter", label: "Offer Letter" },
  { key: "internship_letter", label: "Internship Letter" },
  { key: "nda", label: "NDA" },
  { key: "college_id", label: "College ID" },
];

const iconForMime = (mime) => {
  if (!mime) return "DOC";
  if (mime.includes("pdf")) return "PDF";
  if (mime.includes("image")) return "IMG";
  if (mime.includes("word") || mime.includes("doc")) return "DOCX";
  return "FILE";
};

export default function DocumentVerification({
  documents = [],
  onRefresh,
  canUpload = false,
  targetUserId = null,
  targetEmpId = null,
}) {
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(null);
  const [uploadType, setUploadType] = useState("offer_letter");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const docMap = {};
  documents.forEach((d) => {
    docMap[d.doc_type] = d;
  });

  const uploadedDocs = documents;
  const verifiedCount = uploadedDocs.filter((d) => d.status === "approved" || d.status === "available").length;
  const requiredTypes = ALL_DOC_TYPES.filter((t) => t.required);
  const allRequiredPresent = requiredTypes.every((t) => docMap[t.key]);

  const viewDoc = async (docId) => {
    try {
      await api.previewDocById(docId);
    } catch (e) {
      alert("Could not preview document: " + e.message);
    }
  };

  const downloadDoc = async (docId, name) => {
    try {
      const data = await api.getDocUrl(docId);
      if (data?.url) await api.downloadFile(data.url, name || "document");
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  };

  const reviewDoc = async (docId, status, comment = "") => {
    setProcessing(docId);
    try {
      await api.reviewDoc(docId, status, comment);
      setRejectModal(null);
      setRejectReason("");
      onRefresh?.();
    } catch (e) {
      alert("Review failed: " + e.message);
    } finally {
      setProcessing(null);
    }
  };

  const uploadHrDocument = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      await api.uploadDocumentForUser(targetUserId, targetEmpId, uploadType, uploadFile);
      setUploadFile(null);
      onRefresh?.();
    } catch (e) {
      alert("Upload failed: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const StatusCard = ({ docType }) => {
    const doc = docMap[docType.key];
    let label = "Not uploaded";
    let color = "#94a3b8";
    if (doc) {
      if (doc.status === "approved" || doc.status === "available") {
        label = "Verified";
        color = "#2563eb";
      } else if (doc.status === "rejected") {
        label = `Rejected${doc.review_comment ? ` - ${doc.review_comment}` : ""}`;
        color = "#dc2626";
      } else {
        label = "Pending";
        color = "#d97706";
      }
    }

    return (
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
          {docType.label}
          {docType.required ? <span style={{ fontSize: 10, color: "#dc2626", marginLeft: 8 }}>required</span> : null}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color }}>{label}</div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20 }}>
      {canUpload && (
        <div style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, background: "#f8fafc" }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>HR Document Upload</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} style={{ height: 34, borderRadius: 8, border: "1px solid #cbd5e1", padding: "0 10px" }}>
              {HR_UPLOAD_TYPES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            <button onClick={uploadHrDocument} disabled={!uploadFile || uploading} style={{ height: 34, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", padding: "0 12px", cursor: uploadFile ? "pointer" : "not-allowed" }}>
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Document Presence Check</div>
          <div style={{ fontSize: 12, color: allRequiredPresent ? "#2563eb" : "#dc2626" }}>
            {allRequiredPresent ? "All required documents present" : "Missing required documents"}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {ALL_DOC_TYPES.map((dt) => (
            <StatusCard key={dt.key} docType={dt} />
          ))}
        </div>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Uploaded Documents ({uploadedDocs.length})</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{verifiedCount}/{uploadedDocs.length} verified</div>
        </div>

        {uploadedDocs.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 10, color: "#94a3b8" }}>
            No documents uploaded yet
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {uploadedDocs.map((doc) => {
              const isVerified = doc.status === "approved" || doc.status === "available";
              const isRejected = doc.status === "rejected";
              const isProcessing = processing === doc.id;
              return (
                <div key={doc.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 8, background: "#f1f5f9", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700 }}>
                    {iconForMime(doc.mime_type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{doc.doc_type || "other"}</div>
                    <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {doc.doc_name}
                    </div>
                    {isRejected && doc.review_comment ? (
                      <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>{doc.review_comment}</div>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button onClick={() => viewDoc(doc.id)} style={{ borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                      Preview
                    </button>
                    <button onClick={() => downloadDoc(doc.id, doc.doc_name)} style={{ borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                      Download
                    </button>
                    {!isVerified && (
                      <button onClick={() => reviewDoc(doc.id, "approved")} disabled={isProcessing} style={{ borderRadius: 6, border: "none", background: "#2563eb", color: "#fff", padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                        Approve
                      </button>
                    )}
                    {!isRejected && (
                      <button onClick={() => { setRejectModal(doc); setRejectReason(""); }} disabled={isProcessing} style={{ borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rejectModal ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "grid", placeItems: "center", zIndex: 2000 }}>
          <div style={{ width: "min(420px, 94vw)", background: "#fff", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#dc2626", marginBottom: 8 }}>Reject Document</div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>{rejectModal.doc_type} - {rejectModal.doc_name}</div>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejection"
              style={{ width: "100%", borderRadius: 8, border: "1px solid #fecaca", padding: 10, boxSizing: "border-box", marginBottom: 10 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => { setRejectModal(null); setRejectReason(""); }} style={{ borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", padding: "6px 10px", cursor: "pointer" }}>
                Cancel
              </button>
              <button
                onClick={() => reviewDoc(rejectModal.id, "rejected", rejectReason)}
                disabled={!rejectReason.trim() || processing}
                style={{ borderRadius: 6, border: "none", background: rejectReason.trim() ? "#ef4444" : "#fca5a5", color: "#fff", padding: "6px 10px", cursor: rejectReason.trim() ? "pointer" : "not-allowed" }}
              >
                Reject Document
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
